import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Clock, Download, User } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { locales, ogLocale, type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { BASE_URL } from "@/i18n/head";
import { ORG_ID } from "@/lib/schema";
import { blogStrings, dateLocaleTag } from "@/i18n/content/blog";
import { type BlogPost } from "@/data/blog/types";
import { articlePath, hasContent, postsForLocale, readingMinutes } from "@/data/blog/posts";
import { Img, SIZES } from "@/components/Img";
import { KalenderForm } from "@/components/KalenderForm";

function formatDate(iso: string, locale: Locale): string {
  return new Date(iso).toLocaleDateString(dateLocaleTag[locale], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** <head> for an article: meta + OG article tags + canonical + hreflang +
 *  BlogPosting, FAQPage and BreadcrumbList JSON-LD. */
export function articleHead(post: BlogPost, locale: Locale) {
  const c = post.content[locale]!;
  const url = BASE_URL + articlePath(post, locale);
  const modified = post.dateModified ?? post.datePublished;
  const t = blogStrings[locale];

  const alternates = locales
    .filter((l) => hasContent(post, l))
    .map((l) => ({ rel: "alternate", hrefLang: l, href: BASE_URL + articlePath(post, l) }));

  const blogPosting = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: c.title,
    description: c.description,
    image: BASE_URL + post.heroImage,
    datePublished: post.datePublished,
    dateModified: modified,
    author: { "@type": "Person", name: post.author },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: locale,
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "MI Support", item: BASE_URL + pathFor("home", locale) },
      { "@type": "ListItem", position: 2, name: t.indexTitle, item: BASE_URL + pathFor("artikler", locale) },
      { "@type": "ListItem", position: 3, name: c.title, item: url },
    ],
  };

  const scripts: { type: string; children: string }[] = [
    { type: "application/ld+json", children: JSON.stringify(blogPosting) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
  ];
  if (c.faq?.length) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }

  return {
    meta: [
      { title: `${c.title} | MI Support` },
      { name: "description", content: c.description },
      { property: "og:title", content: c.title },
      { property: "og:description", content: c.description },
      { property: "og:image", content: BASE_URL + post.heroImage },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:locale", content: ogLocale[locale] },
      { property: "article:published_time", content: post.datePublished },
      { property: "article:modified_time", content: modified },
      { property: "article:author", content: post.author },
      ...(post.tags ?? []).map((tag) => ({ property: "article:tag", content: tag })),
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: url },
      ...alternates,
      { rel: "alternate", hrefLang: "x-default", href: BASE_URL + articlePath(post, "da") },
    ],
    scripts,
  };
}

export function ArticlePage({ post, locale }: { post: BlogPost; locale: Locale }) {
  const c = post.content[locale]!;
  const t = blogStrings[locale];
  const related = postsForLocale(locale)
    .filter((p) => p.id !== post.id)
    .slice(0, 2);

  return (
    <SiteLayout>
      <article>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-24 h-[480px] w-[480px] rounded-full bg-primary/10 blur-3xl"
          />
          <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20">
            <Link
              to={pathFor("artikler", locale)}
              className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> {t.backToBlog}
            </Link>
            <div className="mt-8 grid items-center gap-10 md:grid-cols-2 md:gap-12">
              <div>
                {post.tags?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-semibold"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
                <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                  {c.title}
                </h1>
                <p className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed">
                  {c.description}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground/70">
                  <span className="inline-flex items-center gap-2">
                    <User className="h-4 w-4" /> {t.by} {post.author}
                  </span>
                  <time
                    dateTime={post.datePublished}
                    className="sr-only"
                  >
                    {formatDate(post.datePublished, locale)}
                  </time>
                  <span className="inline-flex items-center gap-2">
                    <Clock className="h-4 w-4" /> {t.readingTime(readingMinutes(c))}
                  </span>
                </div>
              </div>
              <div className="relative">
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-xl ring-1 ring-white/10">
                  <Img
                    src={post.heroImage}
                    alt={c.heroAlt}
                    className="h-full w-full object-cover"
                    sizes={SIZES.split}
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Body */}
        <div className="mx-auto max-w-3xl px-4 md:px-8 py-12 space-y-6">
          {c.body.map((b, i) => {
            switch (b.type) {
              case "heading":
                return <h2 key={i} className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">{b.text}</h2>;
              case "paragraph":
                return <p key={i} className="text-lg leading-relaxed text-foreground/85">{b.text}</p>;
              case "list":
                return (
                  <ul key={i} className="space-y-2 pl-1">
                    {b.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-lg text-foreground/85">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                );
              case "quote":
                return (
                  <blockquote key={i} className="border-l-4 border-primary pl-6 my-8">
                    <p className="text-xl italic leading-relaxed text-foreground/90">“{b.text}”</p>
                    {b.cite ? <footer className="mt-3 text-sm text-foreground/60">— {b.cite}</footer> : null}
                  </blockquote>
                );
              case "image":
                return <Img key={i} src={b.src} alt={b.alt} className="w-full rounded-2xl object-cover my-6" sizes={SIZES.split} />;
              case "download":
                return (
                  <p key={i} className="my-6">
                    <a href={b.href} download className="btn-cta">
                      {b.label} <Download className="h-4 w-4" />
                    </a>
                  </p>
                );
            }
          })}
          {post.id === "mi-support-kalender" && locale === "da" && (
            <div className="mt-10 rounded-2xl bg-secondary p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Få kalenderen tilsendt i fysisk format
              </h2>
              <p className="mt-3 text-foreground/85 leading-relaxed">
                Udfyld formularen nedenfor, og vi sørger for, at en fysisk kalender sendes afsted
                til dig.
              </p>
              <div className="mt-6">
                <KalenderForm />
              </div>
            </div>
          )}
        </div>

        {/* FAQ */}
        {c.faq?.length ? (
          <section className="bg-section-light text-section-light-foreground py-14">
            <div className="mx-auto max-w-3xl px-4 md:px-8">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{t.faqHeading}</h2>
              <Accordion type="single" collapsible className="mt-6">
                {c.faq.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-lg font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-base leading-relaxed text-section-light-foreground/85">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        ) : null}

        {/* CTA */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{t.ctaHeading}</h2>
            <p className="mt-4 text-foreground/85 leading-relaxed">{t.ctaBody}</p>
            <Link to={pathFor("kontakt", locale)} className="mt-8 btn-cta">
              {t.ctaButton} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-white/10 py-14">
            <div className="mx-auto max-w-5xl px-4 md:px-8">
              <h2 className="text-2xl font-bold tracking-tight">{t.relatedHeading}</h2>
              <div className="mt-8 grid gap-8 md:grid-cols-2">
                {related.map((p) => {
                  const rc = p.content[locale]!;
                  return (
                    <Link
                      key={p.id}
                      to={articlePath(p, locale)}
                      className="group rounded-2xl bg-secondary overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
                    >
                      <Img src={p.heroImage} alt={rc.heroAlt} className="w-full h-48 object-cover" sizes={SIZES.split} />
                      <div className="p-6">
                        <h3 className="font-bold group-hover:text-primary transition-colors">{rc.title}</h3>
                        <p className="mt-2 text-sm text-foreground/70 line-clamp-2">{rc.description}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </article>
    </SiteLayout>
  );
}

export function ArticleNotFound({ locale }: { locale: Locale }) {
  const t = blogStrings[locale];
  return (
    <SiteLayout>
      <section className="py-32 text-center">
        <h1 className="text-4xl font-bold">404</h1>
        <Link to={pathFor("artikler", locale)} className="mt-6 inline-block text-primary underline">
          {t.backToBlog}
        </Link>
      </section>
    </SiteLayout>
  );
}
